import React, { useState } from "react";
import { List } from "immutable";
import {
  Box,
  Button,
  Input,
  Menu,
  MenuItem,
  Select,
  SelectOption,
  Separator,
  Switch,
  Text,
} from "@showitinc/showit-wc";

import { PropNames } from "@showitinc/merge-utils";
import AppConstants from "@/constants/AppConstants";
import UIActions from "@/actions/UIActions";
import AppStore from "@/stores/AppStore";
import useStoreState from "@/components/providers/useStoreState";
import DynamicInput from "@/app2components/base/DynamicInput";
import ButtonStateTabset from "@/app2components/ButtonStateTabset/ButtonStateTabset";
import { MapElementProperties } from "@/utils/Element/ElementPropertyMapper";
import {
  FORM_FIELD_CHECKBOX_GROUPS,
  FORM_FIELD_DROPDOWN_PANEL_GROUPS,
  FORM_FIELD_RADIO_GROUPS,
  FORM_FIELD_TEXT_FIELD_GROUPS,
  FORM_STATE_OPTIONS,
  filterApplicableFormFieldProps,
  getFormFieldStateProps,
  toControlProps,
} from "@/utils/Element/FormFieldPropertyMaps";
import { LAYOUTS, MIXED } from "@/constants/DesignConstants.mjs";
import { deleteProp } from "@/utils/PropUpdateUtils";
import { elementSyncUtils } from "@/utils/SyncUtils";
import { initFormatter } from "@/utils/PropertyFormatters";
import {
  FORM_FIELD_DATA_TYPE_DATE,
  FORM_FIELD_DATA_TYPE_EMAIL,
  FORM_FIELD_DATA_TYPE_TEL,
  FORM_FIELD_DATA_TYPE_TEXT,
  FORM_FIELD_INPUT_HEIGHTS,
  FORM_FIELD_MULTILINE_HEIGHTS,
  FORM_STATE_DEFAULT,
  SEEDED_FORM_INPUT_CONTENT,
} from "@/constants/FormFieldConstants.mjs";
import FormFieldOptionRow from "./FormFieldOptionRow";
import { moveItem } from "./FormFieldProperties.utils";
import styles from "./FormFieldProperties.module.css";

const { LABEL_POSITION } = PropNames;

const {
  ELEMENT_TYPE_FORM_CHECKBOX,
  ELEMENT_TYPE_FORM_INPUT,
  ELEMENT_TYPE_FORM_SELECT,
  ELEMENT_TYPE_FORM_RADIO,
  THEME_TAB_FORM_FIELDS,
  UPDATE_ACTION_SET,
} = AppConstants;

const MULTI_LINE_TYPE = "multiline";

const DATA_TYPE_OPTIONS = [
  { value: FORM_FIELD_DATA_TYPE_TEXT, label: "Single Line Text" },
  { value: FORM_FIELD_DATA_TYPE_EMAIL, label: "Email" },
  { value: FORM_FIELD_DATA_TYPE_TEL, label: "Phone" },
  { value: FORM_FIELD_DATA_TYPE_DATE, label: "Date" },
  { value: MULTI_LINE_TYPE, label: "Multi-Line Text" },
];

const CUSTOMIZE_GROUPS_BY_TYPE = {
  [ELEMENT_TYPE_FORM_INPUT]: FORM_FIELD_TEXT_FIELD_GROUPS,
  [ELEMENT_TYPE_FORM_SELECT]: FORM_FIELD_DROPDOWN_PANEL_GROUPS,
  [ELEMENT_TYPE_FORM_RADIO]: FORM_FIELD_RADIO_GROUPS,
  [ELEMENT_TYPE_FORM_CHECKBOX]: FORM_FIELD_CHECKBOX_GROUPS,
};

/**
 * @typedef {import("@/utils/Element/ButtonPropertyMapper").MappedElementLayout} MappedProp
 */

/**
 * @param {import("immutable").Map=} mapped - A `MapElementProperties` result.
 * @param {string} layout
 * @returns {MappedProp[]} The layout's mapped props, or none when nothing was mapped.
 */
const mappedLayoutProps = (mapped, layout) => {
  const props = mapped?.get(layout);
  return Array.isArray(props) ? props : [];
};

/**
 * Takes each value, sub props included, from the same prop mapped off another element.
 * @param {MappedProp} mapped
 * @param {MappedProp} source - The same prop, mapped off the element whose values to show.
 * @returns {MappedProp}
 */
const takeValues = (mapped, source) => ({
  ...mapped,
  value: source.value,
  subProps: Object.fromEntries(
    Object.entries(mapped.subProps).map(([name, subProp]) => [
      name,
      takeValues(subProp, source.subProps[name]),
    ]),
  ),
});

/**
 * The form field section of the element panel. The top controls edit the field's content: type,
 * label, placeholder, name, required and options. Below them, the state tabs pick Default, Focus or
 * Error, and each style prop the field stores for that state shows as a control; the rest wait on
 * the Customize menu. Every value comes from the element's property mapper, which reads it the way
 * the stage draws it.
 * @param {Object} props
 * @param {import("immutable").Map} props.element - The selected field, or several conjoined.
 * @param {import("immutable").Map=} props.mappedElement - The `MapElementProperties` result.
 * @param {string} props.layout - `desktop` or `mobile`.
 * @param {boolean} props.isLocked
 * @param {Function} props.onPropUpdate - The element panel's updater.
 * @param {import("immutable").Map} props.theme
 */
const FormFieldPropertiesPanel = ({
  element,
  mappedElement,
  layout,
  isLocked,
  onPropUpdate,
  theme,
}) => {
  const fieldState = useStoreState(AppStore, AppStore.getFormFieldState);
  const [newCustomProps, setNewCustomProps] = useState([]);
  const storedOptions = element.getIn(["content", "options"]);
  const optionsMixed = storedOptions === MIXED;
  const optionsLocked = isLocked || optionsMixed;
  const options = List.isList(storedOptions) ? storedOptions.toArray() : [];

  const type = element.get("type");
  const isInput = type === ELEMENT_TYPE_FORM_INPUT;
  const hasOptions = type === ELEMENT_TYPE_FORM_SELECT || type === ELEMENT_TYPE_FORM_RADIO;

  const readContent = (key) => {
    const value = element.getIn(["content", key]);
    return value === MIXED ? undefined : value;
  };
  const updateContent = (key) => (e) =>
    onPropUpdate({ name: `content.${key}`, value: e.target.value });
  const dataType = readContent("multiLine")
    ? MULTI_LINE_TYPE
    : (readContent("dataType") ?? FORM_FIELD_DATA_TYPE_TEXT);
  const isDate = isInput && dataType === FORM_FIELD_DATA_TYPE_DATE;
  const hasPlaceholder = (isInput && !isDate) || type === ELEMENT_TYPE_FORM_SELECT;
  const onDataTypeSelect = (e) => {
    const isMultiLine = e.detail.value === MULTI_LINE_TYPE;
    const nextDataType = isMultiLine ? FORM_FIELD_DATA_TYPE_TEXT : e.detail.value;
    const currentSeed =
      SEEDED_FORM_INPUT_CONTENT[readContent("dataType") ?? FORM_FIELD_DATA_TYPE_TEXT] ?? {};
    const nextSeed = SEEDED_FORM_INPUT_CONTENT[nextDataType];
    const heights = isMultiLine ? FORM_FIELD_MULTILINE_HEIGHTS : FORM_FIELD_INPUT_HEIGHTS;
    const multiLineChanged = isMultiLine !== Boolean(readContent("multiLine"));

    onPropUpdate([
      { name: "content.dataType", value: nextDataType },
      isMultiLine ? { name: "content.multiLine", value: 1 } : deleteProp("content.multiLine"),
      ...["label", "placeholder"]
        .filter(
          (key) => readContent(key) === currentSeed[key] && nextSeed[key] !== currentSeed[key],
        )
        .map((key) => ({ name: `content.${key}`, value: nextSeed[key] })),
      ...(multiLineChanged
        ? LAYOUTS.map((view) => ({ name: `${view}.h`, value: heights[view] }))
        : []),
    ]);
  };

  const saveOptions = (nextOptions) => {
    if (optionsMixed) return;
    onPropUpdate({ name: "content.options", value: nextOptions, action: UPDATE_ACTION_SET });
  };
  const addOption = () => saveOptions([...options, ""]);
  const removeOption = (index) => saveOptions(options.filter((_, i) => i !== index));
  const moveOption = (fromIndex, toIndex) => {
    if (fromIndex !== toIndex) saveOptions(moveItem(options, fromIndex, toIndex));
  };
  const renameOption = (index) => (e) =>
    saveOptions(options.map((label, i) => (i === index ? e.target.value : label)));

  const { getPropName } = elementSyncUtils(
    element,
    onPropUpdate,
    layout,
    undefined,
    undefined,
    theme,
  );
  const { onChange } = initFormatter(onPropUpdate, getPropName);
  const controlContext = { onFormatChange: onChange, palette: theme?.get("colors")?.toJS() };
  const allMapped = mappedElement?.get("_allMappedElements") ?? [mappedElement];
  const isStored = (name) =>
    allMapped.some(
      (mapped) =>
        mappedLayoutProps(mapped, layout).find((prop) => prop.name === name)?.isInherited === false,
    );

  // A multi-selection is mapped off its first field; show the conjoined values (or MIXED) instead.
  const combinedProps = mappedElement?.has("_allMappedElements")
    ? mappedLayoutProps(MapElementProperties(element, element.get("id"), theme, layout), layout)
    : [];
  const mappedProps = mappedLayoutProps(mappedElement, layout).map((prop) => {
    const combined = combinedProps.find(({ name }) => name === prop.name);
    return combined ? takeValues(prop, combined) : prop;
  });
  const findMapped = (name) => mappedProps.find((prop) => prop.name === name);

  const labelPosition = findMapped(LABEL_POSITION).value;
  const typeGroups = CUSTOMIZE_GROUPS_BY_TYPE[type] ?? [];
  const customizeGroups = typeGroups.map(({ title, props }) => ({
    title,
    props: getFormFieldStateProps(
      filterApplicableFormFieldProps(props, { labelPosition }),
      fieldState,
    ),
  }));
  const hasCustomization = (name) => newCustomProps.includes(name) || isStored(name);
  const customizedProps = customizeGroups.flatMap(({ props }) => props).filter(hasCustomization);
  const resettableProps = typeGroups
    .flatMap(({ props }) => getFormFieldStateProps(props, fieldState))
    .filter(hasCustomization);

  const addCustomization = (name) => setNewCustomProps([...newCustomProps, name]);
  const removeCustomizations = (names) => {
    setNewCustomProps(newCustomProps.filter((propName) => !names.includes(propName)));
    allMapped.forEach((mapped) => {
      mapped?.get("removeCustomizations")?.(names);
    });
  };

  /**
   * @param {MappedProp} mapped
   * @returns {Object} The propData a `DynamicInput` renders from.
   */
  const toPropData = (mapped) => ({
    ...toControlProps(mapped, controlContext),
    disabled: isLocked,
    removeCustomization: () => removeCustomizations([mapped.name]),
  });

  /** The customize groups, split into the props already customized and the ones on the menu. */
  const splitGroups = (isCustomized) =>
    customizeGroups
      .map(({ title, props }) => ({
        title,
        props: props
          .filter((name) => customizedProps.includes(name) === isCustomized)
          .map(findMapped),
      }))
      .filter(({ props }) => props.length > 0);
  const customizedGroups = splitGroups(true);
  const customizeMenuGroups = splitGroups(false);

  const renderGroupTitle = (title, className = "") => (
    <Text
      key={`heading-${title}`}
      variant="heading-2"
      variantStyle="strong"
      className={`${styles.customizeHeading} ${className}`}
    >
      {title}
    </Text>
  );

  return (
    <div data-testid="form-field-properties">
      <div className={styles.section}>
        {isInput && (
          <div className={styles.controlRow}>
            <Select
              label="Type"
              shift="left"
              value={dataType}
              onSISelect={onDataTypeSelect}
              disabled={isLocked}
              data-e2e="form-field-type-select"
              data-testid="form-field-type-select"
            >
              {DATA_TYPE_OPTIONS.map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          </div>
        )}

        <div className={styles.controlRow}>
          <Input
            label="Label"
            value={readContent("label") ?? ""}
            onSIChange={updateContent("label")}
            disabled={isLocked}
            data-e2e="form-field-label-input"
            data-testid="form-field-label-input"
          />
        </div>

        {hasPlaceholder && (
          <div className={styles.controlRow}>
            <Input
              label="Placeholder"
              value={readContent("placeholder") ?? ""}
              onSIChange={updateContent("placeholder")}
              disabled={isLocked}
              data-e2e="form-field-placeholder-input"
              data-testid="form-field-placeholder-input"
            />
          </div>
        )}

        <div className={styles.controlRow}>
          <Input
            label="Field Name"
            placeholder="e.g. email, first_name"
            value={readContent("name") ?? ""}
            onSIChange={updateContent("name")}
            disabled={isLocked}
            data-e2e="form-field-name-input"
            data-testid="form-field-name-input"
          />
        </div>

        <div className={styles.controlRow}>
          <Switch
            label="Required"
            checked={Boolean(readContent("required"))}
            onSIChange={(e) =>
              onPropUpdate(
                e.detail.value
                  ? { name: "content.required", value: 1 }
                  : deleteProp("content.required"),
              )
            }
            disabled={isLocked}
            data-e2e="form-field-required-toggle"
            data-testid="form-field-required-toggle"
          />
        </div>

        {hasOptions && (
          <>
            <Text className="panel-subheading">Options</Text>
            <div className={styles.optionsSection} data-testid="form-field-options-section">
              {options.map((label, index) => (
                <FormFieldOptionRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: options are bare labels and can repeat
                  key={index}
                  index={index}
                  label={label}
                  disabled={optionsLocked}
                  onRename={renameOption(index)}
                  onRemove={() => removeOption(index)}
                  onMove={moveOption}
                />
              ))}
              <Button
                variant="minimal"
                iconPrefix="add"
                iconSize="16"
                onClick={addOption}
                disabled={optionsLocked}
                className={styles.addOptionButton}
                data-e2e="form-field-add-option"
                data-testid="form-field-add-option"
              >
                Add Option
              </Button>
            </div>
          </>
        )}
      </div>

      <Separator />

      <div className={styles.editDefaultsRow}>
        <Button
          variant="link"
          className={styles.editDefaultsButton}
          onClick={() => UIActions.showTheme(THEME_TAB_FORM_FIELDS)}
          data-e2e="form-field-edit-defaults"
          data-testid="form-field-edit-defaults"
        >
          Edit Defaults
        </Button>
      </div>

      <div className={styles.stateTabset}>
        <ButtonStateTabset
          buttonState={fieldState}
          setButtonState={UIActions.setFormFieldState}
          options={FORM_STATE_OPTIONS}
          resetState={FORM_STATE_DEFAULT}
          fullWidth
          testId="form-field-instance-tab"
        />
      </div>

      {customizedGroups.flatMap(({ title, props }) => [
        renderGroupTitle(title, styles.customizedHeading),
        ...props.map((prop) => (
          <DynamicInput key={prop.name} propData={toPropData(prop)} layout={layout} />
        )),
      ])}

      <Box d="flex" justify="center" flex="col" align="center" mb="1" mt="3" pr="1">
        {customizeMenuGroups.length > 0 && (
          <Menu shift="left" py="lg" className={styles.customizeMenu}>
            <Button
              iconPrefix="add"
              iconSize="20"
              variant="minimal"
              slot="trigger-element"
              popovertarget="menu"
              disabled={isLocked}
              data-e2e="form-field-customize"
              data-testid="form-field-customize"
            >
              Customize
            </Button>
            {customizeMenuGroups.flatMap(({ title, props }, index) => [
              ...(index > 0 ? [<MenuItem key={`divider-${title}`} divider />] : []),
              <MenuItem key={`heading-${title}`} heading className={styles.customizeMenuHeading}>
                <span className={styles.customizeMenuHeadingText}>{title}</span>
              </MenuItem>,
              ...props.map((prop) => (
                <MenuItem key={prop.name} onClick={() => addCustomization(prop.name)}>
                  {prop.label}
                </MenuItem>
              )),
            ])}
          </Menu>
        )}

        {resettableProps.length > 0 && (
          <Button
            fullWidth
            variant="link"
            onClick={() => removeCustomizations(resettableProps)}
            className={styles.resetToDefaultButton}
            disabled={isLocked}
            data-e2e="form-field-reset-defaults"
            data-testid="form-field-reset-defaults"
          >
            Reset to Defaults
          </Button>
        )}
      </Box>
    </div>
  );
};

/**
 * Keyed by element so newly added controls reset when the selection changes.
 * @param {Object} props
 * @param {string} props.elementId
 */
const FormFieldProperties = ({ elementId, ...props }) => (
  <FormFieldPropertiesPanel key={elementId} {...props} />
);

export default FormFieldProperties;
